import { useApiBlobDownload, useApiGet, useApiQuery } from 'src/api';
import { useBuildVars } from 'src/config';
import { useMutation } from 'react-query';
import { useTranslate } from '@im/react-localization';
import useSnackBar from './useSnackBar';
import { downloadBlob } from 'src/util/fileUtil';
import { useGetRegion } from 'src/util/getRegion';

export type GetFilesParams = {
  countryCode: string;
  headerId: number;
  multiFile: boolean;
};

export type GetFilesResponse = {
  files: {
    filePath: string;
    status: 'INITIATED' | 'UPLOADED' | 'COMPLETED' | 'FAILED';
  }[];
};

export function useGetFiles() {
  const buildVars = useBuildVars();
  const region = useGetRegion();
  const apiGet = useApiGet<GetFilesResponse>({
    host: buildVars.VP_PARSING_API_BASE,
  });

  return useMutation((params: GetFilesParams) =>
    apiGet(
      '/system.webapi.parserhub/getDocumentFileV2',
      {
        ...params,
        'IM-Region': region,
      },
      {
        'IM-Region': region,
      }
    )
  );
}

export function useFetchFiles(params: GetFilesParams) {
  const buildVars = useBuildVars();
  const region = useGetRegion();
  return useApiQuery<GetFilesResponse>(
    '/system.webapi.parserhub/getDocumentFileV2',
    {
      ...params,
      'IM-Region': region,
    },
    { enabled: params.headerId !== 0 },
    { 'IM-Region': region },
    { host: buildVars.VP_PARSING_API_BASE }
  );
}

export const useDownloadFile = () => {
  const buildVars = useBuildVars();
  const apiBlobDownload = useApiBlobDownload({
    host: buildVars.VP_PARSING_API_BASE,
  });
  const region = useGetRegion();

  return useMutation(
    (params: Omit<GetFilesParams, 'headerId'> & { filePath: string }) =>
      apiBlobDownload(
        '/system.webapi.parserhub/getDocumentFileV2',
        {
          ...params,
          'IM-Region': region,
        },
        {
          'IM-Region': region,
        }
      )
  );
};

export function useFileDownload() {
  const downloadFile = useDownloadFile();
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();

  const onDownloadClick = async ({
    countryCode,
    fileName,
  }: {
    countryCode: string;
    fileName: string;
  }) => {
    showSnackBar({
      autoHide: 'stay',
      message: translate('Common.Downloading'),
      severity: 'success',
    });
    try {
      const res = await downloadFile.mutateAsync({
        countryCode,
        filePath: fileName,
        multiFile: true,
      });
      downloadBlob(res, fileName?.split('/').pop() ?? '');
      showSnackBar({
        message: translate('Common.DownloadSuccess'),
        severity: 'success',
      });
    } catch {
      showSnackBar({
        message: translate('Common.DownloadError'),
        severity: 'error',
      });
    }
  };
  return { onDownloadClick };
}
